import React, { useState, useEffect, useRef } from 'react';

// level 4 이상 + 2시간 고정 세션(120분 이상) 여부.
// - 매 호출마다 localStorage를 읽는 이유: user_level은 useLoad가 부팅 직후 비동기로 기록하므로 클로저 고정값은 stale 위험
// - logoutTimeMinutes >= 120 조건: 배포 이전 짧은 세션(예: 20분)에 10분 임계가 걸려 유휴 중반에 모달이 뜨는 것 방지
const isFixedLongSession = () =>
  Number(localStorage.getItem("user_level")) >= 4 &&
  Number(localStorage.getItem("logoutTimeMinutes")) >= 120;

// 세션 만료 경고(남은시간 배지·경고 모달) 노출 임계값(초) — 고정 세션은 10분 전, 그 외 기본 5분 전
const getWarningThresholdSeconds = () => (isFixedLongSession() ? 10 * 60 : 5 * 60);

const LogoutTimer = ({ 
  logoutTimeStored, 
  onForceLogout, 
  onExtendTime, 
  logoutTimeMinutes,
  lastActivityTime 
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!logoutTimeStored) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      const chkTime = new Date();
      let logoutTimeSet = String(logoutTimeStored).indexOf("-") > 0 || 
                        String(logoutTimeStored).indexOf(".") > 0 
        ? String(logoutTimeStored).replace(/[-.]/g, "/") 
        : logoutTimeStored;
      const logoutTime = new Date(logoutTimeSet);

      const diffMs = logoutTime - chkTime;
      const diffSeconds = Math.floor(diffMs / 1000);
      
      setRemainingSeconds(diffSeconds);

      const warningThreshold = getWarningThresholdSeconds();

      if (diffSeconds <= warningThreshold && diffSeconds > 0 && !showWarning) {
        setShowWarning(true);
      }

      if (chkTime >= logoutTime || diffSeconds <= 0) {
        clearInterval(intervalRef.current);
        onForceLogout();
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [logoutTimeStored, showWarning, onForceLogout]);

  const formatRemainingTime = (seconds) => {
    if (seconds === null || seconds < 0) return '';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}시간 ${minutes}분 ${secs}초`;
    } else if (minutes > 0) {
      return `${minutes}분 ${secs}초`;
    }
    return `${secs}초`;
  };

  const handleExtendClick = () => {
    setShowWarning(false);
    // 다음 1초 틱 전까지 연장 이전의 낡은 남은시간이 배지에 잔상으로 남는 것 방지 — 틱이 새 값을 다시 채움
    setRemainingSeconds(null);
    onExtendTime();
  };

  if (!localStorage.getItem("token") || remainingSeconds === null || remainingSeconds <= 0) {
    return null;
  }

  // 배지 주황색 전환도 경고 모달 노출 시점과 일치
  const warningThreshold = getWarningThresholdSeconds();

  // level 4 이상(2시간 고정 세션)은 잔여 10분 전까지 남은시간 배지 자체를 숨김 — 10분부터 배지+경고 모달 노출.
  // 단, 경고 모달이 이미 떠 있으면(showWarning) 우발적 활동으로 세션이 연장돼도 숨기지 않음 —
  // 명시적으로 로그아웃/시간연장을 선택할 때까지 모달이 유지되던 기존 불변식 보존
  if (isFixedLongSession() && remainingSeconds > warningThreshold && !showWarning) {
    return null;
  }

  return (
    <>
      <div style={{
        position: 'fixed',
        top: '70px',
        right: '20px',
        backgroundColor: remainingSeconds <= 60 ? '#f44336' :
                       remainingSeconds <= warningThreshold ? '#ff9800' : '#4caf50',
        color: 'white',
        padding: '10px 20px',
        borderRadius: '8px',
        zIndex: 9999,
        fontSize: '14px',
        fontWeight: 'bold',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        animation: remainingSeconds <= 60 ? 'pulse 1s infinite' : 'none'
      }}>
        🕐 남은 시간: {formatRemainingTime(remainingSeconds)}
      </div>

      {showWarning && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.stopPropagation();
            }
          }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <h2 style={{
              margin: '0 0 16px 0',
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              ⚠️ 세션 만료 경고
            </h2>
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#f44336',
                margin: '0 0 12px 0'
              }}>
                {formatRemainingTime(remainingSeconds)} 후 자동 로그아웃됩니다
              </p>
              <p style={{
                fontSize: '14px',
                color: '#666',
                margin: '0 0 8px 0'
              }}>
                계속 사용하시려면 "시간 연장" 버튼을 눌러주세요.
              </p>
              {lastActivityTime && (
                <p style={{
                  fontSize: '12px',
                  color: '#999',
                  margin: 0
                }}>
                  마지막 활동: {new Date(lastActivityTime).toLocaleTimeString('ko-KR')}
                </p>
              )}
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  sessionStorage.setItem('isAutoLogout', 'true');
                  onForceLogout();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#666',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
              >
                로그아웃
              </button>
              <button
                onClick={handleExtendClick}
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
              >
                시간 연장 ({logoutTimeMinutes}분)
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </>
  );
};

export default LogoutTimer;